import type {
	SRT_Row,
	SRT_RowSelectionState,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type {
	FilterRuleModel,
	GlobalPCSetModel,
	OptimizationModel,
	PricingCampaignModel,
	ProductsQueryItemModel,
	ProductsQueryRequestModel,
} from '@/shared/generated'
import type { Updater } from '@tanstack/react-table'

import { useCallback, useEffect, useEffectEvent, useMemo } from 'react'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import fastEq from 'fast-deep-equal'
import {
	entries,
	every,
	find,
	groupBy,
	includes,
	isEmpty,
	keys,
	map,
	mapValues,
	pipe,
	prop,
	uniqBy,
} from 'lodash/fp'
import {
	ChartNoAxesCombinedIcon,
	CircleDollarSignIcon,
	DownloadIcon,
	InfoIcon,
	RotateCcwIcon,
	SearchXIcon,
	UploadIcon,
	UserPenIcon,
	ZapIcon,
} from 'lucide-react'
import memoizeOne from 'memoize-one'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import {
	getNodeId,
	getRowId,
	unwrapLines,
	useAssortmentListQuery,
} from '@/entities/assortment'
import {
	columnsQueries,
	FieldsEditorModal,
	formatFilterRule,
	isFilterbaleField,
	prepareList,
	toAdaptedSchema,
} from '@/entities/column'
import {
	getOptimizationsMap,
	useBulkOptimizationMutation,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useSalesEntitiesQuery } from '@/entities/optimization-group'
import {
	useAssignProductsMutation,
	useGlobalPcSetsQuery,
	usePricingCampaignUnassignProducts,
} from '@/entities/pricing-campaign'
import { Access, useAccess } from '@/entities/session'
import { useStoredFiltersKeys } from '@/entities/user'
import { VideoTutorials } from '@/entities/video-tutorial'

import { GlobalPricingAlertsTrigger } from '@/widgets/global-pricing-alerts-trigger'
import { getFieldConfig, QuickFiltersBar } from '@/widgets/quick-filters-bar'
import { SavedReportIndicator } from '@/widgets/saved-report-indicator'
import { SavedReportPicker } from '@/widgets/saved-report-picker'
import { ProductEastsideDrawer } from '@/widgets/sku-sidebar'

import { client } from '@/shared/api/client'
import ProductsAssignActionsDropdownMenu from '@/shared/components/common/ProductsAssignActionsDropdownMenu'
import SelectionMessagePanel from '@/shared/components/data-grid/SelectionMessagePanel'
import TableFilters from '@/shared/components/data-grid/TableFilters'
import TextSearchField from '@/shared/components/data-grid/TextSearchField'
import { ColumnsDrawer } from '@/shared/components/datasheet/components/ColumnsDrawer'
import { MasterTable } from '@/shared/components/master-table'
import { Operators } from '@/shared/components/query-builder'
import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { ButtonGroup } from '@/shared/components/ui/button-group'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Footer, Header, Layout } from '@/shared/components/ui/layout'
import { Loader } from '@/shared/components/ui/loader'
import {
	PaginationControlled,
	usePagination,
} from '@/shared/components/ui/pagination-controlled'
import { Separator } from '@/shared/components/ui/separator'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	HiddenOnContext,
	OptimizationAction,
	OptimizationStatus,
	SavedFilterType,
	VideoTag,
} from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useSelectedItemsFilters } from '@/shared/hooks/useSelectedItemsFilters'
import { useUpdateEffect } from '@/shared/hooks/useUpdateEffect'
import { ampli } from '@/shared/lib/ampli'
import { DatasheetProvider } from '@/shared/lib/datasheet/context'
import { useDatasheetTable } from '@/shared/lib/datasheet/lib/useDatasheetTable'
import { useStore } from '@/shared/lib/datasheet/store'
import { useDrawer } from '@/shared/lib/drawer-manager'
import { useModals } from '@/shared/lib/modal-manager'
import Sentry from '@/shared/lib/sentry'
import { useAssortmentTableConfigStore } from '@/shared/lib/table-config'
import { getTree } from '@/shared/lib/tree'
import { getInitialTableState } from '@/shared/tools/columns'
import { formatNumber } from '@/shared/tools/locale'
import { errorMessage } from '@/shared/tools/message'

import { useColumns } from '../lib/useColumns'
import { useOptimizationUpdateChannel } from '../lib/useOptimizationUpdateChannel'
import { GlobalAssortmentSaveChanges } from './GlobalAssortmentSaveChanges'
import GlobalAssortmentUnassignedItems from './GlobalAssortmentUnassignedItems'
import { OptimizationActionsDropdown } from './OptimizationActionsDropdown'

type AssignProductsToNewPcDraft = {
	optimizationGroupId?: string | null
	scenarioId?: string
	draft?: {
		name?: string
		engine?: string
		assignment_filters?: FilterRuleModel[]
	}
}

const linesToProducts = memoizeOne(unwrapLines, fastEq)

const parseRowIds = (rowIds: string[]): string[] =>
	rowIds.map((id: string): string => id.split('__')[2]).filter(Boolean)
const getSelected = (rowSelection: SRT_RowSelectionState) => keys(rowSelection)

export default function GlobalAssortmentPage() {
	const storedUserConfigs = useAssortmentTableConfigStore((state) => ({
		columnVisibility: state.columnVisibility,
		columnPinning: state.columnPinning,
		columnOrder: state.columnOrder,
		columnSizing: state.columnSizing,
	}))
	const { data: schema } = useSuspenseQuery(
		columnsQueries.list(HiddenOnContext.Assortment)
	)

	const initialState = useMemo(() => {
		const defaultPreset = getInitialTableState(schema)

		if (!isEmpty(storedUserConfigs)) {
			return {
				...storedUserConfigs,
				defaultPreset,
				schema,
				rawSchema: schema,
			}
		} else {
			return {
				...defaultPreset,
				defaultPreset,
				schema,
				rawSchema: schema,
			}
		}
	}, [storedUserConfigs, schema])

	return (
		<DatasheetProvider
			{...initialState}
			datasheetId='global-assortment-products'
		>
			<GlobalAssortmentPageContent />
		</DatasheetProvider>
	)
}
function GlobalAssortmentPageContent() {
	const { t } = useTranslation()

	useOptimizationUpdateChannel()

	const rowSelection = useStore((state) => state.rowSelection)
	const setRowSelection = useStore((state) => state.setRowSelection)
	const isAllProductsSelected = useStore((state) => state.isAllProductsSelected)
	const skuPanelContent = useStore((state) => state.sidebarContent)

	const setIsAllProductsSelected = useStore(
		(state) => state.setIsAllProductsSelected
	)

	const queryClient = useQueryClient()
	const { showModal } = useModals()
	const schema = useStore((state) => state.schema)
	const salesEntitiesQuery = useSalesEntitiesQuery()
	const posIds = useMemo(() => {
		const data = salesEntitiesQuery.data
		const posIdsField = schema?.find((field) => field.id === 'pos_ids')
		if (!posIdsField || !data) return []
		const list = prepareList(data.sales_entities, posIdsField)

		return getTree(list)
	}, [schema, salesEntitiesQuery.data])
	const [storedQuickFiltersKeys, saveStoredQuickFiltersKeys] =
		useStoredFiltersKeys('quick_filters_keys')
	const [{ limit, offset, search: searchText, filters, sorting }, setQuery] =
		useLocationQuery()
	const setFilters = (filters: FilterRuleModel[]) =>
		setQuery({ filters: filters?.length ? filters : null })
	const [searchTextValue] = useDebounceValue(searchText, 1000)
	const { checkPermissons } = useAccess()

	const hasPermissionToUpdate = checkPermissons([
		'EDIT_GLOBAL_PRICING_CAMPAIGNS_SETTINGS',
	])
	const hasPermissionToCreate = checkPermissons([
		'CREATE_GLOBAL_PRICING_CAMPAIGNS',
	])
	const onParamsChanged = useEffectEvent(() => {
		setRowSelection({})
		setIsAllProductsSelected(false)
	})
	useUpdateEffect(() => {
		setQuery({ offset: 0 })
	}, [filters, searchTextValue])
	useEffect(() => {
		onParamsChanged()
	}, [limit, offset, searchTextValue, filters, sorting])
	const productsFiltersToAssign = useMemo(() => {
		if (isAllProductsSelected) {
			return filters.length
				? filters
				: [
						{
							name: 'sku',
							operation: Operators.IS_NOT_EMPTY,
							value: 1,
						},
					]
		}

		return [
			{
				name: 'sku',
				operation: Operators.IN,
				value: parseRowIds(getSelected(rowSelection)),
			},
		]
	}, [rowSelection, isAllProductsSelected, filters])
	const requestBody: ProductsQueryRequestModel = {
		limit,
		offset,
		filters,
		search: searchTextValue || undefined,
		order_by:
			sorting.map(({ id, desc }) => ({
				name: id,
				direction: desc ? 'desc' : 'asc',
			})) ?? [],
	}
	const optimizationsQuery = useOptimizationsQuery()
	const assortmentQuery = useAssortmentListQuery(requestBody, {
		placeholderData: (previousData) => previousData,
	})
	const { data: globalPcSetId, isLoading: isGlobalPcSetIdLoading } =
		useGlobalPcSetsQuery({
			select: pipe(find<GlobalPCSetModel>({ is_main: true }), prop('id')),
		})
	const assignProducts = useAssignProductsMutation({
		onError() {
			toast.error(
				t('global.pc.assign.error', {
					defaultValue: 'Error while assigning products to pricing campaign',
				})
			)
		},
	})
	const unassignProducts = usePricingCampaignUnassignProducts()
	const bulkAction = useBulkOptimizationMutation({
		onSuccess() {
			setRowSelection({})
		},
	})

	const isFetching =
		assortmentQuery.isFetching ||
		optimizationsQuery.isLoading ||
		isGlobalPcSetIdLoading

	const productsCountToRevert = isAllProductsSelected
		? formatNumber(assortmentQuery.data?.total_count || 0)
		: getSelected(rowSelection).length

	const finishedOgIds = useMemo(
		() =>
			optimizationsQuery.data
				?.filter(({ status }) => status === OptimizationStatus.Finished)
				?.map(({ optimization_group_id }) => optimization_group_id) || [],
		[optimizationsQuery.data]
	)
	const hasFinishedLockedOptimizations = useMemo(
		() =>
			optimizationsQuery.data?.some(
				({ status }) =>
					status === OptimizationStatus.Finished ||
					status === OptimizationStatus.Locked
			),
		[optimizationsQuery.data]
	)
	const isAllSelectedProductsFinished = useMemo(
		() =>
			getSelected(rowSelection).length &&
			every(
				(id) => includes(id, finishedOgIds),
				map(
					(item) => parseRowId(item).optimization_group_id,
					getSelected(rowSelection)
				)
			),
		[rowSelection, finishedOgIds]
	)
	const handleClearSelected = () => {
		setRowSelection({})
		setIsAllProductsSelected(false)
	}
	const handleAllProductsSelection = () => {
		setIsAllProductsSelected(true)
	}
	const getSelectedFilters = useSelectedItemsFilters()
	const handlePricingCampaignAssignProducts = async (
		campaign: PricingCampaignModel | string
	) => {
		if (campaign === '') {
			const answer = await confirm({
				title: t('confirm.unassign_pricing_campaigns.title'),
				description: t('confirm.unassign_pricing_campaigns.subtitle'),
				actionText: t('general_confirm'),
				cancelText: t('general_cancel'),
			})
			if (answer) {
				ampli.tableUnassignFromPricingCampaigns()
				await unassignProducts.mutateAsync({
					filters: productsFiltersToAssign,
				})
				queryClient.invalidateQueries({
					queryKey: ['assortment-list'],
				})
				queryClient.invalidateQueries({
					queryKey: ['products-counts-by-pc'],
				})
				handleClearSelected()
			}
		} else if (typeof campaign !== 'string') {
			try {
				const answer = await confirm({
					title: t('confirm.assign_to.static.title'),
					description: t('confirm.assign_to.static.subtitle'),
					actionText: t('general_confirm'),
					cancelText: t('general_cancel'),
				})
				if (answer) {
					ampli.tableAssignToPricingCampaign({
						'Assign to': 'Existing',
					})
					await assignProducts.mutateAsync({
						pricingCampaignId: campaign.id,
						data: {
							filters: productsFiltersToAssign,
						},
					})
					queryClient.invalidateQueries({
						queryKey: ['assortment-list'],
					})
					queryClient.invalidateQueries({
						queryKey: ['products-counts-by-pc'],
					})
					handleClearSelected()
				}
			} catch (error) {
				Sentry.captureException(error)
			}
		}
	}
	const assignPricingCampaignProxyAction =
		(callback: (p: any) => void) => (params: any) => {
			setTimeout(() => {
				showModal('AFFECTED_OPTIMIZATION_GROUPS_MODAL', {
					title: t('global.pc.affect.modal.title'),
					subtitle: t('global.pc.affect.modal.subtitle'),
					getOptimizationGroupsIds: () =>
						client.products
							.getProductsCountByPc({
								filters: productsFiltersToAssign,
							})
							.then((items) => uniqBy('optimization_group_id', items))
							.then(map(prop('optimization_group_id')))
							.then(
								(optimizationGroupsIds) =>
									optimizationGroupsIds as unknown as string[]
							),

					onConfirm: () => callback(params),
				})
			}, 500)
		}
	const handleExportReportClick = () => {
		ampli.globalAssortmentDownloadAssortmentReport()
		showModal('EXPORT_ASSORTMENT', {
			requireCallback: false,
			defaultName: 'Assortment',
			tableConfig: useAssortmentTableConfigStore.getState(),
			context: {
				filters,
			},
		})
	}
	const handleAssignToExistedPricingCampaign = useCallback(
		assignPricingCampaignProxyAction(handlePricingCampaignAssignProducts),
		[productsFiltersToAssign, showModal]
	)
	const handleAssignToNewPricingCampaign = useCallback(
		assignPricingCampaignProxyAction(
			({ draft }: AssignProductsToNewPcDraft) => {
				showModal('PRICING_CAMPAIGN_MODAL', {
					draft: {
						...(draft || {}),
						assignment_filters: productsFiltersToAssign,
					},
					globalPcSetId,
				})
			}
		),
		[productsFiltersToAssign, showModal]
	)
	const handleAssignToAnchorManagement = () => {
		ampli.globalProductsAssignToAnchors()
		const products = keys(rowSelection)?.reduce(
			(acc: Record<string, string[]>, item) => {
				const { optimization_group_id, sku } = parseRowId(item)
				if (!acc[optimization_group_id]) {
					acc[optimization_group_id] = []
				}
				acc[optimization_group_id].push(sku)

				return acc
			},
			{} as Record<string, string[]>
		)
		showModal('ASSIGN_TO_ANCHOR_MANAGEMENT_MODAL', {
			products,
		})
		setRowSelection({})
	}
	const handleRevertPricies = async () => {
		const answer = await confirm({
			title: t('confirm.revert_prices.title', {
				defaultValue: 'Revert to current price',
			}),
			description: t('confirm.revet_prices.description', {
				defaultValue:
					'The recommended prices will be changed to the current prices. Do you want to proceed?',
			}),
			cancelText: t('general_cancel'),
			actionText: t('general_revert'),
		})
		if (answer) {
			try {
				if (isAllProductsSelected) {
					const ogsMap = getOptimizationsMap(optimizationsQuery.data)
					const payload = await client.products
						.getProductsCountByPc({ filters })
						?.then((items) => uniqBy('optimization_group_id', items))
						?.then(
							map(({ optimization_group_id }) => ({
								action: OptimizationAction.RevertPrices,
								optimization_group_id: optimization_group_id,
								optimization_id: ogsMap?.[optimization_group_id as string].id,
								filters,
							}))
						)
					if (!payload) {
						return
					}

					return await bulkAction.mutateAsync(payload)
				}
				const payload = getRevertPricesPayload(
					getSelected(rowSelection),
					getOptimizationsMap(optimizationsQuery.data)
				)
				if (!payload.length) {
					return
				}
				await bulkAction.mutateAsync(payload)
			} catch {
				toast.error(errorMessage(t), {
					duration: 3000,
				})
			}
		}
	}
	const { columns, dataChoices } = useColumns(schema || [])
	const pagination = usePagination({
		total: assortmentQuery.data?.total_count ?? 0,
		limit: Number(requestBody.limit),
		offset: Number(requestBody.offset),
		onChange: (props) => {
			if (isAllProductsSelected) {
				setRowSelection({})
				setIsAllProductsSelected(false)
			}

			return setQuery(props)
		},
	})
	/* 	const tableConfig = useAssortmentTableConfigStore(
		useColumnsController(schema)
	) */
	const children = useMemo(
		() => groupBy(getNodeId, linesToProducts(assortmentQuery.data?.data || [])),
		[assortmentQuery.data?.data]
	)
	const data = assortmentQuery.data?.data || []
	const getChildTreeItems = (row: ProductsQueryItemModel) => {
		if (row?.fields?.optimization_group_id) {
			return children[getNodeId(row)] || []
		}

		return undefined
	}
	const products = useMemo(
		() =>
			linesToProducts(assortmentQuery.data?.data).map((row) => getRowId(row)),
		[assortmentQuery.data?.data]
	)
	const showSubHeader =
		!!products.length &&
		products.every((id) => getSelected(rowSelection).includes(id)) &&
		products.length >= (requestBody?.limit || 0)
	const getRowClassName = useCallback(
		({ row }: { row: SRT_Row<ProductsQueryItemModel> }) => {
			const selectedRowLineId = skuPanelContent?.line_id

			return selectedRowLineId && row.original.line_id === selectedRowLineId
				? 'bg-primary-50 dark:bg-primary-950'
				: ''
		},
		[skuPanelContent]
	)

	const table = useDatasheetTable<ProductsQueryItemModel>({
		enableExpandAll: true,
		enableExpanding: true,
		enableRowSelection: true,
		enableSelectAll: true,
		enableMultiRowSelection: true,
		selectAllMode: 'tree',
		enableColumnOrdering: true,
		columns: columns || [],
		data,
		sorting,
		onSortingChange: (updater: Updater<SRT_SortingState>) => {
			const newSorting = (
				typeof updater === 'function' ? updater(sorting) : updater
			) as SRT_SortingState

			setQuery({
				sorting: newSorting,
			})
		},
		isLoading: isFetching,
		getRowId: (row, index) => {
			if ('srt-row-spacer' in row) return `_${index}`

			return getRowId(row)
		},
		getSubRows: (row) => {
			if ('srt-row-spacer' in row || 'sku' in row) return undefined

			return getChildTreeItems(row) as ProductsQueryItemModel[] | undefined
		},
		getRowClassName,
		onSaveConfig: (id, config) =>
			useAssortmentTableConfigStore.setState(config),
	})
	const filterFieldsChoices = useMemo(
		() => ({ ...dataChoices, pos_ids: posIds || [] }),
		[dataChoices, posIds]
	)
	const fieldSchema = useMemo(() => schema?.filter(isFilterbaleField), [schema])
	const handleOpenBulkModal = () =>
		showModal('UPDATE_ASSORTMENT', {
			filters: getSelectedFilters(filters),
		})
	const handleOpenImportModal = () =>
		showModal('IMPORT_ASSORTMENT', {
			filters: getSelectedFilters(filters),
		})

	return (
		<>
			<Header className='h-11 items-center border-b px-4 py-2'>
				<CircleDollarSignIcon className='size-4' />
				<Separator orientation='vertical' className='mx-2 h-5' />
				<Breadcrumb className='text-sm'>
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbPage>{t('app.products')}</BreadcrumbPage>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
				<ButtonGroup className='ml-auto'>
					<GlobalAssortmentSaveChanges />
					<ButtonGroup>
						<OptimizationActionsDropdown
							rules={filters}
							onApply={setFilters}
							handleExport={handleExportReportClick}
						/>
					</ButtonGroup>
					<ButtonGroup>
						<Button size='icon' onClick={handleExportReportClick}>
							<DownloadIcon />
						</Button>
					</ButtonGroup>
					<ButtonGroup>
						<OptimizationsSummaryTrigger
							disabled={!hasFinishedLockedOptimizations}
						/>
					</ButtonGroup>
					<ButtonGroup>
						<VideoTutorials tags={[VideoTag.Products]}>
							<Button className='ml-auto' variant='ghost' size='icon'>
								<InfoIcon />
							</Button>
						</VideoTutorials>
					</ButtonGroup>
				</ButtonGroup>
			</Header>
			<Layout className='relative flex size-full flex-col overflow-hidden px-4'>
				{assortmentQuery.isError && (
					<Alert variant='destructive' className='mt-2'>
						<AlertDescription>{t('assortment.table.error')}</AlertDescription>
					</Alert>
				)}
				<Blanket
					data-testid={isFetching ? 'blanket-visible' : 'blanket-hidden'}
					className='absolute flex flex-col items-center justify-center space-y-5 rounded-lg bg-transparent'
					isTinted={isFetching}
				>
					<div className='z-50 rounded-lg bg-white/50 p-5 shadow backdrop-blur-md fade-in dark:bg-black/50'>
						<Loader />
					</div>
				</Blanket>
				<Header className='shrink-0 justify-between py-2'>
					<div className='flex w-full items-center justify-between gap-2'>
						<div className='flex min-w-0 items-center gap-2'>
							<div className='flex shrink-0 items-center gap-1'>
								<TextSearchField
									value={searchText}
									onChange={(search) => setQuery({ search })}
								/>
								<SavedReportPicker
									type={SavedFilterType.Products}
									onItemClick={(filterItem) => {
										ampli.savedFiltersClickOnFilter({
											from: 'global assortment',
										})
										setFilters(filterItem.filters!)
									}}
								/>
								<TableFilters
									enableSaveFilters
									savedFiltersType={SavedFilterType.Products}
									onAnalyticsApply={() => ampli.globalAssortmentApplyFilters()}
									choices={filterFieldsChoices}
									schema={fieldSchema || []}
									filters={filters ?? []}
									onApply={setFilters}
								/>
							</div>
							<Separator orientation='vertical' className='h-5! shrink-0' />
							<QuickFiltersBar
								isScrollable
								storedKeys={storedQuickFiltersKeys}
								onSaveStoredKeys={saveStoredQuickFiltersKeys}
								irremovableKeys={[
									'category_ids',
									'brands',
									'pricing_campaign_id',
								]}
								ignoredKeys={['optimization_group_status']}
								rules={filters ?? []}
								fieldsConfig={{
									...getFiledConfig(
										fieldSchema.map((schema) => toAdaptedSchema(schema, t)),
										filterFieldsChoices
									),
									...getFieldConfig(t),
								}}
								onApply={(rules) => {
									setFilters(rules.map(formatFilterRule))
								}}
							/>
							<Separator orientation='vertical' className='h-5! shrink-0' />
							<DropdownMenu>
								<DropdownMenuTrigger
									asChild
									data-testid='assortment-bulk-actions-trigger'
								>
									<Button size='sm' className='shrink-0'>
										<ZapIcon />
										{t('app.bulk_actions', {
											defaultValue: 'Bulk actions',
										})}
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									<DropdownMenuGroup>
										<DropdownMenuLabel>
											{t('general_bulk_update', {
												defaultValue: 'Bulk update',
											})}
										</DropdownMenuLabel>
										<DropdownMenuItem
											data-testid='assortment-bulk-actions-manual-update-trigger'
											onClick={handleOpenBulkModal}
										>
											<UserPenIcon />
											{t('import_dropdown_bulk_update')}
										</DropdownMenuItem>
										<DropdownMenuItem
											data-testid='assortment-bulk-actions-file-update-trigger'
											onClick={handleOpenImportModal}
										>
											<UploadIcon />
											{t('import_dropdown_data_import')}
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<Tooltip>
											<TooltipTrigger asChild>
												<DropdownMenuItem
													data-testid='assortment-bulk-actions-revert-prices-trigger'
													onClick={handleRevertPricies}
													disabled={!isAllSelectedProductsFinished}
												>
													<RotateCcwIcon />
													{`${t('assortment.revert_prices_button', { defaultValue: 'Revert prices' })}`}{' '}
													<DropdownMenuShortcut>{`(${productsCountToRevert})`}</DropdownMenuShortcut>
												</DropdownMenuItem>
											</TooltipTrigger>
											<TooltipContent>
												{t('assortment.revert_prices_button.tooltip', {
													defaultValue:
														'Revert selected prices from current to new',
												})}
											</TooltipContent>
										</Tooltip>
									</DropdownMenuGroup>
								</DropdownMenuContent>
							</DropdownMenu>

							{(!!getSelected(rowSelection)?.length ||
								isAllProductsSelected) && (
								<ProductsAssignActionsDropdownMenu
									isPricingCampaignsActionsDisabled={
										!hasPermissionToUpdate || !hasPermissionToCreate
									}
									isAssignToAnchorManagementDisabled={
										keys(rowSelection)?.length > 20
									}
									scope={{
										only_global: true,
									}}
									onSelectCampaign={handleAssignToExistedPricingCampaign}
									onCreateCampaign={handleAssignToNewPricingCampaign}
									onAssignToAnchorManagement={handleAssignToAnchorManagement}
								/>
							)}
						</div>
						<div className='flex shrink-0 items-center gap-1'>
							<GlobalAssortmentUnassignedItems />
							<SavedReportIndicator
								type={SavedFilterType.Products}
								filters={filters}
								disabled={!filters?.length}
							/>
							<Separator orientation='vertical' className='mx-1 h-5!' />
							<GlobalPricingAlertsTrigger />
							<Access roles={['SuperUser']}>
								<FieldsEditorModal name={HiddenOnContext.Assortment} />
							</Access>
							<ColumnsDrawer table={table} />
						</div>
					</div>
				</Header>
				{showSubHeader && (
					<SelectionMessagePanel
						selectedProducts={getSelected(rowSelection)?.length}
						totalProducts={assortmentQuery.data?.total_count || 0}
						isAllSelected={isAllProductsSelected}
						onClearSelection={handleClearSelected}
						onSelectAllProducts={handleAllProductsSelection}
					/>
				)}
				<div className='border-base relative flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
					<MasterTable table={table} />
					{!data?.length && !isFetching && (
						<div className='absolute inset-0 flex items-center justify-center'>
							<Empty className='z-10 m-auto'>
								<EmptyHeader>
									<EmptyMedia variant='icon'>
										<SearchXIcon className='text-muted-foreground' />
									</EmptyMedia>
									<EmptyTitle>{t('no_data')}</EmptyTitle>
									<EmptyDescription>
										{t('app.no_data.subtitle', {
											defaultValue: 'Try to change filters or search query',
										})}
									</EmptyDescription>
								</EmptyHeader>
							</Empty>
						</div>
					)}
				</div>
				<Footer className='py-1'>
					{pagination && (
						<div className='mr-auto flex items-center gap-6'>
							<PaginationControlled
								className='m-0 w-auto'
								pagination={pagination}
							/>
							<div className='inline-flex items-center gap-1 text-xs'>
								<span>{t('app.items_on_page')}:</span>
								<strong data-testid='page-items-count'>
									{formatNumber(assortmentQuery.data?.data?.length || 0)}
								</strong>
								<strong>{t('general_of')}</strong>
								<strong data-testid='total-items-count'>
									{formatNumber(assortmentQuery.data?.total_count || 0)}
								</strong>
							</div>
							{!!assortmentQuery.data?.total_skus && (
								<div className='inline-flex items-center gap-1 text-xs'>
									<span>
										{t('app.skus_on_page', { defaultValue: 'SKUs on page' })}:
									</span>
									<strong data-testid='page-items-flat-count'>
										{formatNumber(products?.length || 0)}
									</strong>
									<strong>{t('general_of')}</strong>
									<strong data-testid='total-items-flat-count'>
										{formatNumber(assortmentQuery.data?.total_skus)}
									</strong>
								</div>
							)}
						</div>
					)}
					<div className='ml-auto flex items-center space-x-2'>
						<Button
							size='sm'
							className='px-1 py-0'
							data-testid='download-table'
							onClick={handleExportReportClick}
						>
							<DownloadIcon className='mr-1 text-muted-foreground' />
							{t('app.download_table')}
						</Button>
					</div>
				</Footer>
				<ProductEastsideDrawer />
			</Layout>
		</>
	)
}
const OptimizationsSummaryTrigger = ({ disabled }: { disabled?: boolean }) => {
	const drawer = useDrawer('OPTIMIZATIONS_SUMMARY')
	useEffect(
		() => () => {
			drawer.close()
		},
		[]
	)

	return (
		<Button
			variant='primary'
			disabled={disabled}
			size='icon'
			onClick={() => drawer.open({})}
		>
			<ChartNoAxesCombinedIcon />
		</Button>
	)
}

const parseRowId = (id: string) => {
	const [optimization_group_id, , sku] = id.split('__')

	return { optimization_group_id, sku }
}
const groupProductIdsByOg = (ids: string[]) =>
	pipe(
		map((id: string) => parseRowId(id)),
		groupBy('optimization_group_id'),
		mapValues((values: { sku: string }[]) => values.map((v) => v.sku)),
		entries
	)(ids)
const getRevertPricesPayload = (
	products: string[],
	optimization: Record<string, OptimizationModel>
) => {
	if (!products.length) return

	return groupProductIdsByOg(products)?.map(
		([optimization_group_id, value]: [string, string[]]) => ({
			action: OptimizationAction.RevertPrices,
			optimization_group_id,
			optimization_id: optimization[optimization_group_id]?.id,
			filters: [
				{
					name: 'sku',
					operation: Operators.IN,
					value,
				},
			],
		})
	)
}
